import {
  WarningOctagon,
  ArrowUpRight,
  WhatsappLogo,
  Cube,
  Recycle,
  Stack,
  Lightning,
  PlugsConnected,
  Wrench,
} from "@phosphor-icons/react";
import { buildWhatsAppLink } from "../lib/brand";

const MATERIALS = [
  {
    id: "aluminio",
    title: "Alumínio Bloco",
    desc: "Panelas, peças fundidas e refugos de alumínio em geral.",
    Icon: Cube,
    accent: "from-zinc-300 to-zinc-500",
  },
  {
    id: "latinhas",
    title: "Latinhas",
    desc: "Latas de alumínio limpas, prensadas ou soltas.",
    Icon: Recycle,
    accent: "from-zinc-200 to-zinc-400",
  },
  {
    id: "perfil",
    title: "Perfil de Alumínio",
    desc: "Esquadrias, perfis de janela, portas e fachadas.",
    Icon: Stack,
    accent: "from-zinc-300 to-zinc-500",
  },
  {
    id: "cobre-limpo",
    title: "Cobre Limpo",
    desc: "Cobre brilhante, sem oxidação e sem isolamento.",
    Icon: Lightning,
    accent: "from-[#D97743] to-[#8a4d28]",
  },
  {
    id: "cobre-encapado",
    title: "Cobre Encapado",
    desc: "Fios e cabos de cobre com capa plástica.",
    Icon: PlugsConnected,
    accent: "from-[#D97743] to-[#8a4d28]",
  },
  {
    id: "sucata-cobre",
    title: "Cabos & Sucata de Cobre",
    desc: "Motores, bobinas, transformadores e sucata em geral.",
    Icon: Wrench,
    accent: "from-[#D97743] to-[#8a4d28]",
  },
];

export default function Materials() {
  return (
    <section
      id="materiais"
      data-testid="materials-section"
      className="py-20 sm:py-28 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10 sm:mb-14">
          <div>
            <div className="label-eyebrow mb-3">[ O que compramos ]</div>
            <h2 className="font-display font-bold text-white text-3xl sm:text-5xl tracking-tight">
              Materiais que pagamos<br />
              <span className="text-[var(--rc-copper-bright)]">o melhor preço.</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-sm sm:text-base">
            Trabalhamos exclusivamente com metais não-ferrosos. Veja cada tipo
            de material aceito e mande sua foto pelo WhatsApp para cotação.
          </p>
        </div>

        {/* Categories: Alumínio + Cobre header bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {MATERIALS.map((m, i) => {
            const Icon = m.Icon;
            const isCopper = m.id.startsWith("cobre") || m.id.includes("sucata");
            return (
              <a
                key={m.id}
                href={buildWhatsAppLink(
                  `Olá! Tenho ${m.title} para avaliação. Vou mandar a foto.`,
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`material-card-${m.id}`}
                className="group relative bg-[var(--rc-surface)] overflow-hidden hover:bg-[var(--rc-surface-2)] transition-colors p-6 sm:p-8 min-h-[260px] flex flex-col justify-between"
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono-ui text-[10px] tracking-[0.22em] text-zinc-500">
                    /{(i + 1).toString().padStart(2, "0")} ·{" "}
                    {isCopper ? "COBRE" : "ALUMÍNIO"}
                  </span>
                  <ArrowUpRight
                    size={18}
                    weight="bold"
                    className="text-zinc-500 group-hover:text-[var(--rc-copper-bright)] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                  />
                </div>

                <div className="my-7 sm:my-8">
                  <div
                    className={`w-14 h-14 sm:w-16 sm:h-16 grid place-items-center bg-gradient-to-br ${m.accent} shadow-inner`}
                  >
                    <Icon size={30} weight="duotone" className="text-black/85" />
                  </div>
                </div>

                <div>
                  <h3 className="font-display font-bold text-white text-xl sm:text-2xl mb-1.5">
                    {m.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{m.desc}</p>
                </div>

                <div
                  className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[var(--rc-copper-bright)]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-hidden
                />
              </a>
            );
          })}
        </div>

        {/* Não compramos ferro */}
        <div
          data-testid="no-iron-banner"
          className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_auto] items-center gap-6 border border-[var(--rc-warning)]/40 p-6 sm:p-8"
          style={{ background: "rgba(239,68,68,0.06)" }}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 grid place-items-center bg-[var(--rc-warning)]/15 border border-[var(--rc-warning)]/40 text-[var(--rc-warning)] shrink-0">
              <WarningOctagon size={26} weight="duotone" />
            </div>
            <div>
              <div className="font-mono-ui text-[10px] tracking-[0.22em] text-[var(--rc-warning)] mb-1">
                ATENÇÃO
              </div>
              <h3 className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight">
                Não compramos ferro.
              </h3>
              <p className="text-zinc-300 mt-1 text-sm sm:text-base">
                Foco total em alumínio e cobre. Isso garante melhor preço pra você.
              </p>
            </div>
          </div>
          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="materials-whatsapp-btn"
            className="rc-btn-whatsapp inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md"
          >
            <WhatsappLogo size={20} weight="fill" />
            <span className="font-display font-bold">Tirar dúvida no WhatsApp</span>
          </a>
        </div>
      </div>
    </section>
  );
}
